
import { useState } from 'react';
import { CancerDiagnosis, CancerDiagnosisStatus } from '@/types/cancer';
import { mockCancerDiagnoses, mockPatients, Patient } from '../data/mockData';

export const useDiagnosisOperations = () => {
  // Explicitly cast the mock data to ensure status property is of type CancerDiagnosisStatus
  const typedMockDiagnoses = mockCancerDiagnoses as CancerDiagnosis[];
  
  const [cancerDiagnoses, setCancerDiagnoses] = useState<CancerDiagnosis[]>(typedMockDiagnoses);
  const [patients, setPatients] = useState(mockPatients);
  const [diagnosisDialogOpen, setDiagnosisDialogOpen] = useState(false);
  const [diagnosisViewOpen, setDiagnosisViewOpen] = useState(false);
  const [selectedDiagnosis, setSelectedDiagnosis] = useState<CancerDiagnosis | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  
  // Function to handle viewing a diagnosis
  const handleViewDiagnosis = (diagnosisId: string) => {
    const diagnosis = cancerDiagnoses.find(d => d.id === diagnosisId);
    if (diagnosis) {
      setSelectedDiagnosis(diagnosis);
      setDiagnosisViewOpen(true);
    }
  };
  
  // Function to create a new diagnosis
  // Update the function signature to match what we're expecting
  const handleCreateDiagnosis = (diagnosis: Omit<CancerDiagnosis, 'id' | 'createdAt'>) => {
    const newDiagnosis: CancerDiagnosis = {
      ...diagnosis,
      id: `cd${cancerDiagnoses.length + 1}`,
      createdAt: new Date().toISOString(),
      updatedAt: diagnosis.updatedAt || new Date().toISOString(), // Use provided updatedAt or create new
      createdById: diagnosis.createdById || 'unknown-user' // Use provided createdById or default
    };
    setCancerDiagnoses([...cancerDiagnoses, newDiagnosis]);
  };
  
  // Function to update an existing diagnosis
  const handleUpdateDiagnosis = (diagnosis: CancerDiagnosis) => {
    const updatedDiagnoses = cancerDiagnoses.map(d => 
      d.id === diagnosis.id ? { 
        ...diagnosis,
        updatedAt: diagnosis.updatedAt || new Date().toISOString()
      } : d
    );
    setCancerDiagnoses(updatedDiagnoses);
    setIsEditing(false);
    setSelectedDiagnosis(null);
    setDiagnosisViewOpen(false);
  };

  // Function to start editing a diagnosis
  const startEditingDiagnosis = (diagnosis: CancerDiagnosis) => {
    setSelectedDiagnosis(diagnosis);
    setIsEditing(true);
    setDiagnosisDialogOpen(true);
    setDiagnosisViewOpen(false); // Close view dialog
  };
  
  return {
    cancerDiagnoses,
    patients,
    diagnosisDialogOpen,
    setDiagnosisDialogOpen,
    diagnosisViewOpen,
    setDiagnosisViewOpen,
    selectedDiagnosis,
    setSelectedDiagnosis,
    handleViewDiagnosis,
    handleCreateDiagnosis,
    handleUpdateDiagnosis,
    isEditing,
    setIsEditing,
    startEditingDiagnosis
  };
};
